const scheme = (name, dotColor, light, dark) => ({
  name,
  dotColor,
  light,
  dark
});

export const COLOR_SCHEMES = {
  green: scheme("Verde", "#10b981", {
    dotColor: "#10b981",
    badgeBg: "#ecfdf5",
    badgeText: "#047857",
    badgeBorder: "#a7f3d0",
    accentText: "#059669",
    accentBg: "rgba(16, 185, 129, 0.12)",
    accentBorder: "#6ee7b7",
    nodeRing: "rgba(16, 185, 129, 0.4)",
    nodeBg: "#059669",
    nodeText: "#059669",
    gradientFrom: "#10b981",
    gradientTo: "#0d9488",
    glow: "rgba(16, 185, 129, 0.15)"
  }, {
    dotColor: "#34d399",
    badgeBg: "rgba(6, 78, 59, 0.45)",
    badgeText: "#6ee7b7",
    badgeBorder: "rgba(16, 185, 129, 0.35)",
    accentText: "#34d399",
    accentBg: "rgba(6, 78, 59, 0.35)",
    accentBorder: "#047857",
    nodeRing: "rgba(52, 211, 153, 0.35)",
    nodeBg: "#059669",
    nodeText: "#34d399",
    gradientFrom: "#10b981",
    gradientTo: "#0f766e",
    glow: "rgba(16, 185, 129, 0.2)"
  }),
  blue: scheme("Azul", "#2563eb", {
    dotColor: "#2563eb",
    badgeBg: "#eff6ff",
    badgeText: "#1d4ed8",
    badgeBorder: "#bfdbfe",
    accentText: "#2563eb",
    accentBg: "rgba(37, 99, 235, 0.1)",
    accentBorder: "#93c5fd",
    nodeRing: "rgba(37, 99, 235, 0.4)",
    nodeBg: "#2563eb",
    nodeText: "#2563eb",
    gradientFrom: "#2563eb",
    gradientTo: "#4f46e5",
    glow: "rgba(37, 99, 235, 0.15)"
  }, {
    dotColor: "#60a5fa",
    badgeBg: "rgba(30, 58, 138, 0.45)",
    badgeText: "#93c5fd",
    badgeBorder: "rgba(59, 130, 246, 0.35)",
    accentText: "#60a5fa",
    accentBg: "rgba(30, 58, 138, 0.35)",
    accentBorder: "#1d4ed8",
    nodeRing: "rgba(96, 165, 250, 0.35)",
    nodeBg: "#2563eb",
    nodeText: "#60a5fa",
    gradientFrom: "#2563eb",
    gradientTo: "#4338ca",
    glow: "rgba(37, 99, 235, 0.2)"
  }),
  purple: scheme("Roxo", "#8b5cf6", {
    dotColor: "#8b5cf6",
    badgeBg: "#f5f3ff",
    badgeText: "#6d28d9",
    badgeBorder: "#ddd6fe",
    accentText: "#7c3aed",
    accentBg: "rgba(139, 92, 246, 0.1)",
    accentBorder: "#c4b5fd",
    nodeRing: "rgba(139, 92, 246, 0.4)",
    nodeBg: "#7c3aed",
    nodeText: "#7c3aed",
    gradientFrom: "#8b5cf6",
    gradientTo: "#7c3aed",
    glow: "rgba(139, 92, 246, 0.15)"
  }, {
    dotColor: "#a78bfa",
    badgeBg: "rgba(76, 29, 149, 0.45)",
    badgeText: "#c4b5fd",
    badgeBorder: "rgba(139, 92, 246, 0.35)",
    accentText: "#a78bfa",
    accentBg: "rgba(76, 29, 149, 0.35)",
    accentBorder: "#6d28d9",
    nodeRing: "rgba(167, 139, 250, 0.35)",
    nodeBg: "#7c3aed",
    nodeText: "#a78bfa",
    gradientFrom: "#8b5cf6",
    gradientTo: "#6d28d9",
    glow: "rgba(139, 92, 246, 0.2)"
  }),
  amber: scheme("Laranja / Âmbar", "#f59e0b", {
    dotColor: "#f59e0b",
    badgeBg: "#fffbeb",
    badgeText: "#b45309",
    badgeBorder: "#fde68a",
    accentText: "#d97706",
    accentBg: "rgba(245, 158, 11, 0.12)",
    accentBorder: "#fcd34d",
    nodeRing: "rgba(245, 158, 11, 0.4)",
    nodeBg: "#f59e0b",
    nodeText: "#d97706",
    gradientFrom: "#f59e0b",
    gradientTo: "#ea580c",
    glow: "rgba(245, 158, 11, 0.15)"
  }, {
    dotColor: "#fbbf24",
    badgeBg: "rgba(120, 53, 15, 0.45)",
    badgeText: "#fcd34d",
    badgeBorder: "rgba(245, 158, 11, 0.35)",
    accentText: "#fbbf24",
    accentBg: "rgba(120, 53, 15, 0.35)",
    accentBorder: "#b45309",
    nodeRing: "rgba(251, 191, 36, 0.35)",
    nodeBg: "#f59e0b",
    nodeText: "#fbbf24",
    gradientFrom: "#f59e0b",
    gradientTo: "#c2410c",
    glow: "rgba(245, 158, 11, 0.2)"
  })
};

function resolveSchemeKey(colorName) {
  if (!colorName) return "green";
  const key = String(colorName).toLowerCase().trim();
  if (COLOR_SCHEMES[key]) return key;
  if (key.includes("verde") || key.includes("green") || key.includes("emerald")) {
    return "green";
  }
  if (key.includes("azul") || key.includes("blue")) return "blue";
  if (key.includes("roxo") || key.includes("violet") || key.includes("purple")) {
    return "purple";
  }
  if (
    key.includes("laranja") ||
    key.includes("ambar") ||
    key.includes("amber") ||
    key.includes("orange")
  ) {
    return "amber";
  }
  return "green";
}

/**
 * @param {string} [colorName]
 * @param {{ isDark?: boolean }} [options]
 */
export function getColorScheme(colorName, options = {}) {
  const isDark = Boolean(options.isDark);
  const key = resolveSchemeKey(colorName);
  const base = COLOR_SCHEMES[key];
  const tokens = isDark ? base.dark : base.light;
  return {
    key,
    name: base.name,
    dotColor: tokens.dotColor,
    ...tokens
  };
}
